import { inject, Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthService } from './auth-service';
import { StoreesBackendService } from './storees-backend.service';
import { logger } from '../utils/logger';

/**
 * Resets daily push campaign fatigue when the user engages with the app.
 *
 * Detection (common mobile / PWA practice):
 * - **Foreground / resume** — `App.appStateChange` (native) or Page Visibility API (web): user brought the app to the foreground.
 * - **Direct manipulation** — `pointerdown` (capture): first-class signal for taps and stylus; covers most “user did something” cases without listening to every scroll/resize.
 *
 * Reports are **throttled** (default 30 minutes) to limit backend load and battery; one successful report fully resets fatigue for campaign purposes.
 */
@Injectable({
  providedIn: 'root',
})
export class CampaignFatigueActivityService {
  private static readonly MIN_MS_BETWEEN_REPORTS = 30 * 60 * 1000;

  private readonly authService = inject(AuthService);
  private readonly storeesBackendService = inject(StoreesBackendService);

  private started = false;
  private lastReportAt = 0;

  /**
   * Register listeners once, after the user can obtain a JWT.
   */
  async start(): Promise<void> {
    if (this.started) {
      return;
    }
    this.started = true;

    if (Capacitor.isNativePlatform()) {
      await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void this.reportIfDue('resume');
        }
      });
    } else {
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'visible') {
            void this.reportIfDue('visibility');
          }
        },
        { passive: true }
      );
    }

    document.addEventListener(
      'pointerdown',
      () => {
        void this.reportIfDue('gesture');
      },
      { capture: true, passive: true }
    );

    logger.debug('CampaignFatigueActivityService: listeners registered');
  }

  private async reportIfDue(
    source: 'resume' | 'gesture' | 'visibility'
  ): Promise<void> {
    const now = Date.now();
    if (now - this.lastReportAt < CampaignFatigueActivityService.MIN_MS_BETWEEN_REPORTS) {
      return;
    }

    const jwtToken = await this.authService.getSessionJwtToken();
    if (!jwtToken) {
      return;
    }

    this.lastReportAt = now;

    try {
      await this.storeesBackendService.postPushInteraction(jwtToken, {
        campaignType: 'day_story',
        action: 'app_activity',
        source,
      });
      logger.debug('CampaignFatigueActivityService: reported app activity', {
        source,
      });
    } catch (error) {
      this.lastReportAt = 0;
      logger.warn('CampaignFatigueActivityService: failed to report app activity', error);
    }
  }
}
